// Sidebar.js

import React, { useContext, useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './Sidebar.css';

import { useAuth } from '../contexts/AuthContext';
import ActiveItemContext from '../contexts/ActiveItemContext';
import { saveDataToDB } from '../utils/dbUtilities';

/* ────────── Icon set ────────── */
import {
  IoHomeOutline,
  IoCompassOutline,
  IoAddCircleOutline,
  IoLogOutOutline,
  IoPersonAddOutline,
  IoSettingsOutline,
  IoHeartOutline,
  IoPersonOutline
} from 'react-icons/io5';
import { MdFiberNew } from 'react-icons/md';
import { AiOutlineInfoCircle } from 'react-icons/ai';
import { BsFillPersonVcardFill } from 'react-icons/bs';
import { FaThumbtack, FaSquarePollVertical, FaPenToSquare, FaAddressCard, FaDice } from 'react-icons/fa6';

const Sidebar = ({ isVisible, setIsSidebarVisible }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { isLoggedIn, currentUser: authUser, logout, isAdmin } = useAuth();
  const { activeItem, setActiveItem } = useContext(ActiveItemContext);

  const [isDesktop, setIsDesktop] = useState(window.innerWidth > 1180);
  useEffect(() => {
    const handleResize = () => {
      const tablet = window.innerWidth <= 1180;
      setIsDesktop(!tablet);
      setIsSidebarVisible(!tablet);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsSidebarVisible]);

  useEffect(() => {
    document.body.classList.toggle('sidebar-open', isVisible);
  }, [isVisible]);

  const username = isLoggedIn
    ? (authUser?.username || authUser?.displayName || authUser?.email?.split('@')[0])
    : 'Looksaway';

  const handleLogoutClick = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  const baseMenuItems = [
    { id: 'Home', icon: <IoHomeOutline className="sidebar-icon" />, link: '/' },
    { id: 'Post', icon: <IoAddCircleOutline className="sidebar-icon" />, link: '/newlookspost' },
    { id: 'New Posts', icon: <MdFiberNew className="sidebar-icon" />, link: '/newposts', hideOnLargeScreen: true },
    { id: 'Explore', icon: <IoCompassOutline className="sidebar-icon" />, link: '/explore', hideOnLargeScreen: true, hideOnMobile: true },
    { id: 'Discover', icon: <BsFillPersonVcardFill className="sidebar-icon" />, link: '/bio', hideOnLargeScreen: true, hideOnMobile: true },
    { id: 'Polls', icon: <FaSquarePollVertical className="sidebar-icon" />, link: '/lookspolls' },
    { id: 'Info', icon: <AiOutlineInfoCircle className="sidebar-icon" />, link: '/info' },
    { id: 'Random Bio', icon: <FaDice className="sidebar-icon" />, link: '/briefbiorando' } // 👈 Now visible to all
  ];

  const loggedInMenuItems = [
    { id: 'Likes', icon: <IoHeartOutline className="sidebar-icon" />, link: '/lookslikes', hideOnLargeScreen: true },
    { id: 'My Posts', icon: <IoPersonOutline className="sidebar-icon" />, link: '/looksmyposts', hideOnLargeScreen: true },
    { id: 'Logout', icon: <IoLogOutOutline className="sidebar-icon" />, onClick: handleLogoutClick }
  ];

  const loggedOutMenuItems = [
    { id: 'Sign Up', icon: <IoPersonAddOutline className="sidebar-icon" />, link: '/signup' },
    { id: 'Login', icon: <IoLogOutOutline className="sidebar-icon" />, link: '/login' }
  ];

  const menuItems = isLoggedIn
    ? [...baseMenuItems, ...loggedInMenuItems]
    : [...baseMenuItems, ...loggedOutMenuItems];

  const adminItems = [
    {
      id: 'Bulletin',
      icon: <FaThumbtack className="sidebar-icon" />,
      link: '/bulletin'
    },
    {
      id: 'Admin Dashboard',
      icon: <IoSettingsOutline className="sidebar-icon" />,
      link: '/looks-admin-dashboard'
    },
    {
      id: 'Brief Bios',
      icon: <FaAddressCard className="sidebar-icon" />,
      link: '/briefbios'
    },
    {
      id: 'Create Brief Bio',
      icon: <FaPenToSquare className="sidebar-icon" />,
      link: '/briefbioform'
    }
  ];

  const handleItemClick = async (item) => {
    if (window.innerWidth <= 1180) {
      setIsSidebarVisible(false);
      document.body.classList.remove('sidebar-open');
    }
    setActiveItem(item.id);

    if (item.link) {
      if (location.pathname === item.link) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        navigate(item.link);
      }
    } else if (item.onClick) {
      item.onClick();
    }

    if (authUser) {
      try {
        await saveDataToDB('userSettings', { activeItem: item.id }, authUser.uid);
      } catch (err) {
        console.error('Error saving active item:', err);
      }
    }
  };

  const SidebarItem = ({ item }) => {
    const hideLg = item.hideOnLargeScreen && window.innerWidth >= 1180;
    const hideSm = item.hideOnMobile && window.innerWidth <= 1180;
    if (hideLg || hideSm) return null;

    return (
      <li className={`sidebar-item ${activeItem === item.id ? 'active' : ''}`}>
        <Link
          to={item.link || '#'}
          className="sidebar-link"
          onClick={(e) => { e.preventDefault(); handleItemClick(item); }}
        >
          <div className="sidebar-link-content">
            <div className="sidebar-icon-wrapper">{item.icon}</div>
            <span className="sidebar-text">{item.id}</span>
          </div>
        </Link>
      </li>
    );
  };

  return (
    <div className={`sidebar ${isVisible ? 'open' : ''}`}>
      {/* mobile header */}
      <div className="mobile-header">
        <div className="profile-name-container">
          <h3 className="profile-name">{username}</h3>
        </div>
        <button
          className="mobile-close-button"
          onClick={() => {
            setIsSidebarVisible(false);
            document.body.classList.remove('sidebar-open');
          }}
        >
          ✕
        </button>
      </div>

      {/* logo + username (desktop only) */}
      <div className="profile-section">
        {isDesktop && (
          <Link
            to="/"
            className="profile-link"
            style={{ textDecoration: 'none', color: 'inherit' }}
            onClick={(e) => {
              e.preventDefault();
              if (window.innerWidth <= 1180) setIsSidebarVisible(false);
              if (location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
              else navigate('/');
            }}
          >
            <img
              src="/looksaway_logo.png"
              alt="Looksaway Logo"
              className="profile-pic"
            />
            <div className="profile-name-container">
              <h3 className="profile-name">{username}</h3>
            </div>
          </Link>
        )}
      </div>

      {/* menu */}
      <nav>
        <ul className="sidebar-menu">
          {menuItems.map((item) => (
            <SidebarItem key={item.id} item={item} />
          ))}

          {/* Admin-only */}
          {isAdmin && adminItems.map((item) => (
            <SidebarItem key={item.id} item={item} />
          ))}
        </ul>
      </nav>
    </div>
  );
};

export default Sidebar;
